import React, { useState } from 'react';
import { Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import ExcelJS from 'exceljs';

const ExportarART = ({ 
  datosParticipantesMetas,
  datosProyectosMetas,
  metricas,
  filtrosActivos,
  data // datos originales para municipios, departamentos, etc.
}) => {
  const [exporting, setExporting] = useState(false);

  const exportarAExcel = async () => {
    setExporting(true);
    
    try {
      const workbook = new ExcelJS.Workbook();
      
      // Hoja 1: Análisis de Metas por Participante
      const worksheetParticipantes = workbook.addWorksheet('Metas por Participante');
      
      // Hoja 2: Resumen por Proyecto
      const worksheetProyectos = workbook.addWorksheet('Resumen por Proyecto');
      
      // Hoja 3: Métricas y Filtros
      const worksheetMetricas = workbook.addWorksheet('Métricas');
      
      // Estilos comunes
      const estiloTitulo = {
        font: { bold: true, size: 16, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '2C3E50' } },
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      const estiloSubtitulo = {
        font: { bold: true, size: 12 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'ECF0F1' } },
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      const estiloCabecera = {
        font: { bold: true, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '3498DB' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const estiloCelda = {
        alignment: { vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const estiloCeldaCentrada = {
        ...estiloCelda,
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      // --- HOJA 1: METAS POR PARTICIPANTE ---
      // Título
      worksheetParticipantes.mergeCells('A1:J1');
      const tituloParticipantes = worksheetParticipantes.getCell('A1');
      tituloParticipantes.value = 'ANÁLISIS DE METAS POR PARTICIPANTE - VISTA ART';
      tituloParticipantes.style = estiloTitulo;
      
      // Fecha y filtros
      const fecha = new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      worksheetParticipantes.mergeCells('A2:J2');
      worksheetParticipantes.getCell('A2').value = `Generado el: ${fecha}`;
      worksheetParticipantes.getCell('A2').style = {
        font: { italic: true, color: { argb: '7F8C8D' } },
        alignment: { horizontal: 'left' }
      };

      // Filtros aplicados
      if (filtrosActivos && Object.values(filtrosActivos).some(f => f && f !== 'todos')) {
        const filtrosTexto = [];
        if (filtrosActivos.busqueda) filtrosTexto.push(`Búsqueda: "${filtrosActivos.busqueda}"`);
        if (filtrosActivos.municipio && filtrosActivos.municipio !== 'todos') filtrosTexto.push(`Municipio: ${filtrosActivos.municipio}`);
        if (filtrosActivos.departamento && filtrosActivos.departamento !== 'todos') filtrosTexto.push(`Departamento: ${filtrosActivos.departamento}`);
        if (filtrosActivos.proyecto && filtrosActivos.proyecto !== 'todos') filtrosTexto.push(`Proyecto: ${filtrosActivos.proyecto}`);
        
        if (filtrosTexto.length > 0) {
          worksheetParticipantes.mergeCells('A3:J3');
          worksheetParticipantes.getCell('A3').value = `Filtros aplicados: ${filtrosTexto.join(' | ')}`;
          worksheetParticipantes.getCell('A3').style = {
            font: { bold: true, color: { argb: 'E74C3C' } },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FDEffDEC' } }
          };
          worksheetParticipantes.addRow([]);
        }
      }

      // Cabecera de la tabla
      const cabeceraParticipantes = [
        '#',
        'Nombre',
        'Cédula',
        'Municipio',
        'Departamento',
        'Proyecto',
        'Meta Individual',
        'Archivos Subidos',
        '% Cumplido',
        '% Faltante',
        'Estado'
      ];
      
      const filaCabecera = worksheetParticipantes.addRow(cabeceraParticipantes);
      filaCabecera.eachCell((cell) => {
        cell.style = estiloCabecera;
      });

      // Datos de participantes
      datosParticipantesMetas.forEach((participante, index) => {
        const fila = [
          index + 1,
          participante.nombre || '',
          participante.cedula || '',
          participante.municipio || '',
          participante.departamento || '',
          participante.proyectos || '',
          typeof participante.metaIndividual === 'number' ? participante.metaIndividual : 0,
          typeof participante.archivosParticipante === 'number' ? participante.archivosParticipante : 0,
          `${typeof participante.porcentajeCumplido === 'number' ? participante.porcentajeCumplido : 0}%`,
          `${typeof participante.porcentajeFaltante === 'number' ? participante.porcentajeFaltante : 0}%`,
          participante.estado || 'Sin estado'
        ];

        const filaDatos = worksheetParticipantes.addRow(fila);
        
        // Aplicar estilos según el estado
        filaDatos.eachCell((cell, colNumber) => {
          cell.style = estiloCeldaCentrada;
          
          // Colorear según porcentajes
          if (colNumber === 9) { // % Cumplido
            const porcentaje = participante.porcentajeCumplido || 0;
            if (porcentaje >= 100) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D5F4E6' } };
            } else if (porcentaje >= 70) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9C4' } };
            } else if (porcentaje >= 50) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0B2' } };
            } else {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } };
            }
          }
          
          if (colNumber === 10) { // % Faltante
            const porcentaje = participante.porcentajeFaltante || 100;
            if (porcentaje < 20) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D5F4E6' } };
            } else if (porcentaje < 50) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9C4' } };
            } else if (porcentaje < 70) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0B2' } };
            } else {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } };
            }
          }
          
          if (colNumber === 11) { // Estado
            const estado = participante.estado || '';
            if (estado === 'Completado') {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'C8E6C9' } };
            } else if (estado === 'En progreso') {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0B2' } };
            } else if (estado === 'Atrasado') {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } };
            }
          }
        });
      });

      // Totales
      const totalMeta = datosParticipantesMetas.reduce((sum, p) => sum + (p.metaIndividual || 0), 0);
      const totalArchivos = datosParticipantesMetas.reduce((sum, p) => sum + (p.archivosParticipante || 0), 0);
      const porcentajePromedio = totalMeta > 0 ? Math.round((totalArchivos / totalMeta) * 100) : 0;
      
      const filaTotales = worksheetParticipantes.addRow([
        'TOTALES',
        '', '', '', '', '',
        totalMeta,
        totalArchivos,
        `${porcentajePromedio}%`,
        `${Math.max(0, 100 - porcentajePromedio)}%`,
        ''
      ]);
      
      filaTotales.eachCell((cell) => {
        cell.style = {
          ...estiloCeldaCentrada,
          font: { bold: true },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F8F9F9' } }
        };
      });

      // Ajustar anchos
      worksheetParticipantes.columns = [
        { width: 6 },   // #
        { width: 30 },  // Nombre
        { width: 15 },  // Cédula
        { width: 20 },  // Municipio
        { width: 20 },  // Departamento
        { width: 30 },  // Proyecto
        { width: 12 },  // Meta
        { width: 12 },  // Subidos
        { width: 12 },  // % Cumplido
        { width: 12 },  // % Faltante
        { width: 15 }   // Estado
      ];

      // --- HOJA 2: RESUMEN POR PROYECTO ---
      worksheetProyectos.mergeCells('A1:D1');
      worksheetProyectos.getCell('A1').value = 'RESUMEN DE METAS POR PROYECTO';
      worksheetProyectos.getCell('A1').style = estiloTitulo;
      
      // Cabecera proyectos
      const cabeceraProyectos = ['Proyecto', 'Meta Total', 'Ejecutado', '% Avance'];
      const filaCabeceraProyectos = worksheetProyectos.addRow(cabeceraProyectos);
      filaCabeceraProyectos.eachCell((cell) => {
        cell.style = estiloCabecera;
      });

      // Datos proyectos
      datosProyectosMetas.forEach((proyecto) => {
        const fila = [
          proyecto.nombre || proyecto.nombreCorto || '',
          proyecto.meta || 0,
          proyecto.ejecutado || 0,
          `${proyecto.porcentaje || 0}%`
        ];
        
        const filaProyecto = worksheetProyectos.addRow(fila);
        filaProyecto.eachCell((cell, colNumber) => {
          cell.style = estiloCeldaCentrada;
          
          // Colorear según % de avance
          if (colNumber === 4) {
            const porcentaje = proyecto.porcentaje || 0;
            if (porcentaje >= 100) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D5F4E6' } };
            } else if (porcentaje >= 70) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9C4' } };
            } else if (porcentaje >= 50) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0B2' } };
            } else {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } };
            }
          }
        });
      });

      // Ajustar anchos proyectos
      worksheetProyectos.columns = [
        { width: 40 },
        { width: 15 },
        { width: 15 },
        { width: 15 }
      ];

      // --- HOJA 3: MÉTRICAS ---
      worksheetMetricas.mergeCells('A1:B1');
      worksheetMetricas.getCell('A1').value = 'MÉTRICAS GLOBALES - VISTA ART';
      worksheetMetricas.getCell('A1').style = estiloTitulo;
      
      // Métricas principales
      const metricasData = [
        ['Total Participantes', metricas.total],
        ['Visitas Proyectadas', metricas.metaGeneral],
        ['Visitas Realizadas', metricas.archivosEjecutados],
        ['% de Avance General', `${metricas.porcentajeAvance}%`],
        ['Participantes con Archivos', metricas.conArchivos],
        ['Total de Registros', metricas.totalRegistros]
      ];
      
      metricasData.forEach(([label, valor], index) => {
        const fila = worksheetMetricas.addRow([label, valor]);
        
        // Aplicar color al % de avance
        if (index === 3) { // % de Avance
          const porcentaje = metricas.porcentajeAvance || 0;
          fila.getCell(2).style = {
            ...estiloCeldaCentrada,
            font: { bold: true, size: 14 }
          };
          
          if (porcentaje >= 100) {
            fila.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'C8E6C9' } };
          } else if (porcentaje >= 70) {
            fila.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9C4' } };
          } else if (porcentaje >= 50) {
            fila.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0B2' } };
          } else {
            fila.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } };
          }
        } else {
          fila.getCell(2).style = estiloCeldaCentrada;
        }
        
        fila.getCell(1).style = {
          ...estiloCelda,
          font: { bold: true },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F8F9F9' } }
        };
      });

      // Resumen de distribución por estado
      worksheetMetricas.addRow([]);
      worksheetMetricas.mergeCells('A9:B9');
      worksheetMetricas.getCell('A9').value = 'DISTRIBUCIÓN POR ESTADO';
      worksheetMetricas.getCell('A9').style = estiloSubtitulo;
      
      const estados = datosParticipantesMetas.reduce((acc, p) => {
        const estado = p.estado || 'Sin estado';
        acc[estado] = (acc[estado] || 0) + 1;
        return acc;
      }, {});
      
      Object.entries(estados).forEach(([estado, cantidad]) => {
        const porcentaje = (cantidad / datosParticipantesMetas.length * 100).toFixed(1);
        const fila = worksheetMetricas.addRow([estado, `${cantidad} (${porcentaje}%)`]);
        
        // Colorear según estado
        if (estado === 'Completado') {
          fila.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'C8E6C9' } };
        } else if (estado === 'En progreso') {
          fila.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9C4' } };
        } else if (estado === 'Atrasado') {
          fila.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } };
        }
        
        fila.getCell(2).style = estiloCeldaCentrada;
        fila.getCell(1).style = estiloCelda;
      });

      // Ajustar anchos métricas
      worksheetMetricas.columns = [
        { width: 30 },
        { width: 25 }
      ];

      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.href = url;
      link.download = `Analisis_ART_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar URL
      setTimeout(() => URL.revokeObjectURL(url), 100);
      
    } catch (error) {
      console.error('Error al exportar:', error);
      alert('Error al exportar el archivo. Por favor, intente de nuevo.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      type="primary"
      icon={<DownloadOutlined />}
      onClick={exportarAExcel}
      loading={exporting}
      disabled={!datosParticipantesMetas || datosParticipantesMetas.length === 0}
      title="Exportar análisis a Excel"
    >
      Exportar Excel
    </Button>
  );
};

export default ExportarART;